import {
  applyLegalMove,
  generateLegalMoves,
  isGameOver,
  isInCheck,
  positionKey,
} from './rules';
import type { AiLevel, Color, GameState, Move, Piece } from './types';

export interface AiConfig {
  maxDepth: number;
  timeBudgetMs: number;
  topK: number;
  noise: number;
}

export interface SearchResult {
  move: Move;
  depth: number;
  elapsedMs: number;
}

export const AI_CONFIGS: Record<AiLevel, AiConfig> = {
  900: { maxDepth: 2, timeBudgetMs: 120, topK: 4, noise: 0.34 },
  1200: { maxDepth: 3, timeBudgetMs: 300, topK: 3, noise: 0.18 },
  1500: { maxDepth: 4, timeBudgetMs: 650, topK: 2, noise: 0.08 },
  1800: { maxDepth: 5, timeBudgetMs: 1_200, topK: 1, noise: 0.02 },
};

const PIECE_VALUES: Record<Piece['kind'], number> = {
  pawn: 100,
  knight: 320,
  bishop: 330,
  rook: 500,
  queen: 900,
  king: 20_000,
};
const MATE_SCORE = 1_000_000;

class SearchTimeout extends Error {
  constructor() {
    super('search timeout');
  }
}

function checkDeadline(deadline: number): void {
  if (performance.now() >= deadline) throw new SearchTimeout();
}

function colorSign(color: Color, perspective: Color): number {
  return color === perspective ? 1 : -1;
}

function centerBonus(piece: Piece, index: number): number {
  if (piece.kind === 'king' || piece.kind === 'pawn') return 0;
  const file = index % 8;
  const rank = Math.floor(index / 8);
  const distanceFromCenter = Math.abs(file - 3.5) + Math.abs(rank - 3.5);
  return Math.round((7 - distanceFromCenter) * 2);
}

function homeSpace(state: GameState, color: Color): number {
  const ranks = color === 'w' ? [0, 1] : [6, 7];
  return ranks.reduce((count, rank) => (
    count + state.board.slice(rank * 8, rank * 8 + 8).filter((piece) => !piece).length
  ), 0);
}

function evaluate(state: GameState): number {
  const perspective = state.turn;
  if (state.status === 'checkmate') return state.winner === perspective ? MATE_SCORE : -MATE_SCORE;
  if (isGameOver(state)) return 0;

  let material = 0;
  for (let index = 0; index < state.board.length; index += 1) {
    const piece = state.board[index];
    if (!piece) continue;
    material += colorSign(piece.color, perspective) * (PIECE_VALUES[piece.kind] + centerBonus(piece, index));
  }

  const mobility = generateLegalMoves(state).length;
  const checkPressure = isInCheck(state, perspective) ? -35 : 0;
  const ownHomeSpace = homeSpace(state, perspective);
  const opponentHomeSpace = homeSpace(state, perspective === 'w' ? 'b' : 'w');
  const resurrectionSpace = (ownHomeSpace - opponentHomeSpace) * 2;
  const ownScore = perspective === 'w' ? state.whiteScore : state.blackScore;
  const opponentScore = perspective === 'w' ? state.blackScore : state.whiteScore;
  const ownCaptureStreak = perspective === 'w' ? state.whiteCaptureStreak : state.blackCaptureStreak;
  const scorePressure = (ownScore - opponentScore) * 18;
  const streakPressure = ownCaptureStreak * 5;
  return material + mobility * 4 + checkPressure + resurrectionSpace + scorePressure + streakPressure;
}

function moveOrderScore(move: Move, captured: boolean, promotion: boolean, check: boolean): number {
  let score = 0;
  if (captured) score += 1_000;
  if (promotion) score += 800;
  if (check) score += 100;
  score += move.to.charCodeAt(0) + Number(move.to[1] ?? 0);
  return score;
}

interface OrderedMove {
  move: Move;
  child: GameState;
  score: number;
}

function orderedChildren(state: GameState, moves: Move[], deadline: number): OrderedMove[] {
  const children: OrderedMove[] = [];
  for (const move of moves) {
    checkDeadline(deadline);
    const result = applyLegalMove(state, move);
    if (!result.ok || !result.state) continue;
    children.push({
      move,
      child: result.state,
      score: moveOrderScore(
        move,
        !!result.captured,
        !!move.promotion,
        result.state.status === 'check' || result.state.status === 'checkmate',
      ),
    });
  }
  return children.sort((a, b) => b.score - a.score);
}

function quiescence(state: GameState, alpha: number, beta: number, deadline: number, depth: number): number {
  checkDeadline(deadline);
  const standingPat = evaluate(state);
  if (standingPat >= beta) return beta;
  if (standingPat > alpha) alpha = standingPat;
  if (depth <= 0 || isGameOver(state)) return alpha;

  const captures = orderedChildren(state, generateLegalMoves(state), deadline)
    .filter((candidate) => candidate.child.lastMove?.capture || candidate.move.promotion);
  for (const candidate of captures) {
    const score = -quiescence(candidate.child, -beta, -alpha, deadline, depth - 1);
    if (score >= beta) return beta;
    if (score > alpha) alpha = score;
  }
  return alpha;
}

function negamax(state: GameState, depth: number, alpha: number, beta: number, deadline: number): number {
  checkDeadline(deadline);
  if (depth <= 0 || isGameOver(state)) return quiescence(state, alpha, beta, deadline, 2);

  const children = orderedChildren(state, generateLegalMoves(state), deadline);
  if (!children.length) return evaluate(state);
  let best = -Infinity;
  for (const candidate of children) {
    const score = -negamax(candidate.child, depth - 1, -beta, -alpha, deadline);
    if (score > best) best = score;
    if (score > alpha) alpha = score;
    if (alpha >= beta) break;
  }
  return best;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function chooseNoisyMove(state: GameState, scored: Array<{ move: Move; score: number }>, config: AiConfig): Move {
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  const pool = sorted.slice(0, Math.max(1, config.topK));
  if (pool.length === 1 || config.noise === 0) return pool[0]!.move;
  const salt = hashString(`${positionKey(state)}|${config.noise}`);
  const normalized = (salt % 10_000) / 10_000;
  const noisyIndex = Math.min(pool.length - 1, Math.floor(normalized * pool.length * (1 + config.noise)));
  return pool[noisyIndex]!.move;
}

export function findBestMove(state: GameState, level: AiLevel): SearchResult | null {
  const startedAt = performance.now();
  const config = AI_CONFIGS[level];
  const deadline = startedAt + config.timeBudgetMs;
  const legal = generateLegalMoves(state);
  if (!legal.length) return null;

  let completedDepth = 0;
  let bestScored = legal.map((move) => ({ move, score: 0 }));
  for (let depth = 1; depth <= config.maxDepth; depth += 1) {
    try {
      const scored: Array<{ move: Move; score: number }> = [];
      for (const move of legal) {
        checkDeadline(deadline);
        const result = applyLegalMove(state, move);
        if (!result.ok || !result.state) continue;
        scored.push({
          move,
          score: -negamax(result.state, depth - 1, -Infinity, Infinity, deadline),
        });
      }
      if (!scored.length) break;
      bestScored = scored;
      completedDepth = depth;
    } catch (error) {
      if (!(error instanceof SearchTimeout)) throw error;
      break;
    }
  }

  return {
    move: chooseNoisyMove(state, bestScored, config),
    depth: completedDepth,
    elapsedMs: Math.round(performance.now() - startedAt),
  };
}
